import { format as formatDate, isSameDay } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addChannelDetailDate, addChannelDetailItem, getChannelDetailItems } from "@/sideEffects/api";
import { ChannelDetails, DayOfWeek } from "@/lib/types";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { ChannelCalendarItem } from "./ChannelCalendarItem";
import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { ChannelCalendarCustomToolbar } from "./ChannelCalendarCustomToolbar";
import { ChannelYearView } from "./ChannelYearView";
import { BigCalendar } from "@/components/calendar/BigCalendar";
import { convertTimeToUtc, formatDateToLocal__Date } from "@/lib/dateUtils";

interface Props {
  channelDetails: ChannelDetails;
}

export const ChannelCalendarView: React.FC<Props> = ({ channelDetails }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const channelDetailItems = useQuery({
    queryKey: ["getChannelDetailItems", channelDetails.intId],
    queryFn: () => getChannelDetailItems(channelDetails.id, String(activeFirm?.firmGuid)),
  });

  const channelDetailItemsData = channelDetailItems.data?.data.result || [];

  const queryClient = useQueryClient();
  const addEventMutation = useMutation({
    mutationFn: async (values: { start: Date; end: Date }) => {
      const weekDay = values.start.getDay() as DayOfWeek;
      const isOnlyOneDay = isSameDay(values.start, values.end);

      const channelDetailItem = addChannelDetailItem({
        channelId: channelDetails.intId,
        periodStart: formatDate(values.start, "yyyy-MM-dd"),
        periodEnd: formatDate(values.end, "yyyy-MM-dd"),
        isActive: true,
        firmId: String(activeFirm?.firmGuid),
      });

      if (!isOnlyOneDay) {
        return await channelDetailItem;
      }

      const response = await channelDetailItem;
      const channelDetailItems = await getChannelDetailItems(channelDetails.id, String(activeFirm?.firmGuid));

      const channelDetailItemsData = channelDetailItems.data.result || [];
      const newChannelDetailItem = channelDetailItemsData.find((item) => item.id === response.data.newDataId);

      await addChannelDetailDate({
        channelDetailId: Number(newChannelDetailItem?.intId),
        dayOfWeek: weekDay,
        startTime: convertTimeToUtc(values.start.toISOString()),
        endTime: convertTimeToUtc(values.end.toISOString()),
        firmGuid: String(activeFirm?.firmGuid),
      });

      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getChannelDetailItems"] });
      showSuccess(t("common.addedEventSuccess"));
    },
    onError: showApiError,
  });

  const isLoading = channelDetailItems.isLoading || addEventMutation.isPending;

  return (
    <BigCalendar
      isLoading={isLoading}
      events={channelDetailItemsData.map((item) => ({
        title: "",
        start: new Date(formatDateToLocal__Date(item.periodStart)),
        end: new Date(formatDateToLocal__Date(item.periodEnd)),
        ...item,
      }))}
      YearView={ChannelYearView}
      CalendarItem={ChannelCalendarItem}
      CalendarToolbar={ChannelCalendarCustomToolbar}
    />
  );
};
